import React, { useState, useEffect, useCallback } from 'react';
import EntitySearchForm from '../components/entity/EntitySearchForm';
import EntityTable from '../components/entity/EntityTable';
import PreqinSearchForm from '../components/preqin/PreqinSearchForm';
import PreqinQuickResults from '../components/preqin/PreqinQuickResults';
import PreqinDetailView from '../components/preqin/PreqinDetailView';
import useEntitySearch from '../hooks/useEntitySearch';
import usePreqinSearch from '../hooks/usePreqinSearch';
import { Typography, Box, Paper, Tabs, Tab, Link, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Search as SearchIcon, TrendingUp } from '@mui/icons-material';
import { Pagination } from '../components/common';
import WidePageContainer from '../components/layout/WidePageContainer';
import DisclaimerModal from '../components/common/DisclaimerModal';
import useDisclaimer from '../hooks/useDisclaimer';
import { useSearchParams, useNavigate } from 'react-router-dom';

// Must match PAGE_SIZE in backend (see base/settings.py)
// COULDDO: Read page_size from backend response for full decoupling
const PAGE_SIZE = 50;

const Search: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tabValue, setTabValue] = useState(0);
    const [query, setQuery] = useState('');
    const [entityType, setEntityType] = useState<'vc_firm' | 'angel_investor' | 'organization' | 'people_of_interest'>('people_of_interest');
    const [page, setPage] = useState(1);
    const [selectedPreqinResult, setSelectedPreqinResult] = useState<any>(null);
    const { isOpen: disclaimerOpen, openDisclaimer, closeDisclaimer } = useDisclaimer();
    const navigate = useNavigate();

    // Feature flag for Financial Data
    const isFinancialDataEnabled = process.env.REACT_APP_ENABLE_FINANCIAL_DATA === 'true';

    // Initialize state from URL parameters on mount and URL changes
    useEffect(() => {
        const entityTypeParam = searchParams.get('entity_type') as 'vc_firm' | 'angel_investor' | 'organization' | 'people_of_interest';
        const pageParam = searchParams.get('page');
        const queryParam = searchParams.get('query');
        const tabParam = searchParams.get('tab') || '0';
        const tabIndex = parseInt(tabParam);

        // If feature is disabled and user tries to access tab 1, default to tab 0
        if (!isFinancialDataEnabled && tabIndex === 1) {
            setTabValue(0);
        } else {
            setTabValue(tabIndex);
        }

        const validEntityTypes: ('vc_firm' | 'angel_investor' | 'organization' | 'people_of_interest')[] =
            ['people_of_interest', 'vc_firm', 'angel_investor', 'organization'];

        // Update entity type from URL
        if (entityTypeParam && validEntityTypes.includes(entityTypeParam)) {
            setEntityType(entityTypeParam);
        }

        // Update page from URL
        if (pageParam) {
            const pageNumber = parseInt(pageParam, 10);
            if (!isNaN(pageNumber) && pageNumber > 0) {
                setPage(pageNumber);
            }
        }
        // Update query from URL
        if (queryParam !== null) {
            setQuery(queryParam);
        }
    }, [searchParams, isFinancialDataEnabled]);

    // Entity search hook with React Query - only enable when we have valid state from URL
    const hasValidState = entityType && page > 0;
    const { data, isLoading, error } = useEntitySearch(entityType, query, page, hasValidState);
    
    const results = data?.results || [];
    const count = data?.count || 0;
    
    // Preqin search hook  
    const { results: preqinResults, loading: preqinLoading, error: preqinError, quickSearch } = usePreqinSearch();

    const handleSearch = (
        q: string,
        type: 'vc_firm' | 'angel_investor' | 'organization' | 'people_of_interest'
    ) => {
        setQuery(q);
        setEntityType(type);
        setPage(1); // Always reset to page 1 for new searches
        
        // Update URL parameters
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('entity_type', type);
        newSearchParams.set('page', '1');        
        //newSearchParams.delete('query');
        if (q.trim()) {
            newSearchParams.set('query', q);
        } else {
            newSearchParams.delete('query');
        }
        setSearchParams(newSearchParams);
    };

    const handlePreqinSearch = useCallback(async (q: string) => {
        //if(q != null && q.trim().length >= 3) {
        if(q != null) {
            setSelectedPreqinResult(null);
            await quickSearch(q);    
        }

        // // Reset detail view when searching
        //await quickSearch(q);
    }, [quickSearch]);

    const handleSelectPreqinResult = (result: any) => {
        setSelectedPreqinResult(result);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('company', result.name);
        //newSearchParams.set('tab', '0');
        setSearchParams(newSearchParams);
    };

    const handleBackToPreqinSearch = () => {
        setSelectedPreqinResult(null);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('company');
        newSearchParams.delete('cat');
        setSearchParams(newSearchParams);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        
        // Update URL parameters, preserving existing ones
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('page', newPage.toString());
        newSearchParams.set('entity_type', entityType); // Ensure entity type is preserved
        if (query.trim()) {
            newSearchParams.set('query', query);
        }
        setSearchParams(newSearchParams);
    };

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        const newTabParams = new URLSearchParams(searchParams);
        newTabParams.set('tab', newValue.toString());
        setSearchParams(newTabParams);
        setTabValue(newValue);
    };

    const totalPages = Math.ceil(count / PAGE_SIZE);

    return (
        <WidePageContainer>
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography align="center" variant="h4" component="h1" gutterBottom sx={{ fontWeight: 400, mb: 3 }}>
                    Welcome to the Ethics VC Research Platform
                </Typography>
                <Typography align="center" textAlign={'justify'} variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Track and explore venture-capital networks to identify ethical and human rights concerns.
Please read our disclaimer for important information about data verification and platform status.
Current data reflects publicly available information relevant to each category. The Institute is actively building and expanding this database, adding new fields and verified details over time. Tagging for human rights findings, conflict zone links, and exclusion lists is in development and will be introduced in future updates.
                </Typography>
                

                <Typography align="center" variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Please read our{' '}
                    <Link 
                        component="button"
                        variant="body2"
                        onClick={openDisclaimer}
                        sx={{ 
                            color: 'info.main',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            '&:hover': {
                                color: 'info.dark'
                            }
                        }}
                    >
                        disclaimer
                    </Link>
                    {' '}for important information about data verification and platform status.
                </Typography>
                
                <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
                    <Tab
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <SearchIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                Entity Search
                                <Tooltip title="Investigate people, firms, and organizations for ties to Israel, military backgrounds, and industry details">
                                    <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                </Tooltip>
                            </Box>
                        }
                    />
                    {isFinancialDataEnabled && (
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <TrendingUp sx={{ fontSize: 18, color: 'success.main' }} />
                                    Financial Data
                                    <Tooltip title="Explore how institutions (e.g. universities and endowments) invest through private equity and venture capital.">
                                        <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    </Tooltip>
                                </Box>
                            }
                        />
                    )}
                </Tabs>

                {tabValue === 0 ? (
                    <EntitySearchForm
                        onSearch={handleSearch}
                        initialQuery={query}
                        initialEntityType={entityType}
                    />
                ) : isFinancialDataEnabled ? (
                    <PreqinSearchForm onSearch={handlePreqinSearch} />
                ) : null}
            </Paper>

            {tabValue === 0 ? (
                // Entity search results
                isLoading ? (
                    <Typography>Loading...</Typography>
                ) : error ? (
                    <Typography color="error">{error.message}</Typography>
                ) : results && results.length > 0 ? (
                    <>
                        <EntityTable entities={results} entityType={entityType} />
                        {totalPages > 1 && (
                            <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} />
                        )}
                    </>
                ) : (
                    <Typography color="text.secondary">No results found.</Typography>
                )
            ) : isFinancialDataEnabled ? (
                // Preqin search results or detail view
                selectedPreqinResult ? (
                    <PreqinDetailView
                        selectedResult={selectedPreqinResult}
                        onBack={handleBackToPreqinSearch}
                    />
                ) : (
                    <>
                        {preqinError && (
                            <Typography color="error" sx={{ mb: 2 }}>{preqinError}</Typography>
                        )}
                        <PreqinQuickResults
                            results={preqinResults}
                            loading={preqinLoading}
                            onSelectResult={handleSelectPreqinResult}
                        />
                    </>
                )
            ) : null}
            <DisclaimerModal 
                open={disclaimerOpen} 
                onClose={closeDisclaimer} 
            />
        </WidePageContainer>
    );
};

export default Search;
